import { Segment, ActiveStats, SUPPORTED_TAG_TYPES, SegmentType } from './IVbC2paContract';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

export class VbC2paStatsReporter {
	private segmentMap: Record<SegmentType, Map<string, Segment>> = {
		video: new Map<string, Segment>(),
		audio: new Map<string, Segment>(),
		contentVideo: new Map<string, Segment>()
	};
	private overallVerified: Record<SegmentType, boolean> = {
		video: true,
		audio: true,
		contentVideo: true
	};
	private errorSegments: Record<SegmentType, Segment[]> = {
		video: [],
		audio: [],
		contentVideo: []
	};
	private currentStatsSubject$ = new BehaviorSubject<ActiveStats | null>(null);
	private lastCurrentTimeInSec: number = 0;

	private lastActiveSegment: Record<string, Segment> = {
		audio: null,
		video: null,
		contentVideo: null
	};

	private lastActiveSegmentProcessed: Record<string, boolean> = {
		audio: false,
		video: false,
		contentVideo: false
	};

	private currentTimeSub: Subscription;

	public initPlayerCurrentTimeObservable(currentTime$: Observable<number>): void {
		this.currentTimeSub?.unsubscribe();
		this.currentTimeSub = currentTime$.subscribe(t => {
			const seconds = t / 1000;
			this.lastCurrentTimeInSec = seconds;
			this.updateActiveStats(seconds);
		});
	}

	/** Observable with current active segment + overallVerified */
	public get currentStats$(): Observable<ActiveStats | null> {
		return this.currentStatsSubject$.asObservable();
	}

	public reportMp4Stats(activeStats: ActiveStats): void {
		if (!activeStats) {
			return;
		}

		this.currentStatsSubject$.next(activeStats);
	}

	public addVerifiedFragmentToStats(type: SegmentType, stats: Segment['stats']): void {
		if (!this.segmentMap[type] || this.segmentMap[type].size === 0) {
			this.segmentMap[type] = new Map();
			this.overallVerified[type] = true;
		}

		const interval = stats.interval as [number, number];
		const key = `${interval[0]}:${interval[1]}`;

		// Replace duplicate if exists
		if (this.segmentMap[type].has(key)) {
			const oldSeg = this.segmentMap[type].get(key)!;
			this.segmentMap[type].delete(key);

			if (!oldSeg.stats.verified) {
				this.overallVerified[type] = this.recomputeOverallVerified(type);
			}
		}
		const seg: Segment = { interval, stats };
		this.segmentMap[type].set(key, seg);
		if (!stats.verified) {
			this.overallVerified[type] = false;
			this.errorSegments[type] = this.errorSegments[type] || [];
			this.errorSegments[type].push(seg);
		}

		if (interval[0] <= this.lastCurrentTimeInSec && this.lastCurrentTimeInSec <= interval[1]) {
			this.updateActiveStats(this.lastCurrentTimeInSec);
		}
	}

	private recomputeOverallVerified(tag: string): boolean {
		for (const seg of this.segmentMap[tag].values()) {
			if (!seg.stats.verified) {
				return false;
			}
		}
		return true;
	}

	private updateActiveStats(currentTime: number): void {
		for (const type of SUPPORTED_TAG_TYPES) {
			const map = this.segmentMap[type];

			if (!map || map.size === 0) {
				continue;
			}

			let active: Segment | null = null;

			for (const seg of map.values()) {
				if (seg.interval[0] <= currentTime && currentTime <= seg.interval[1]) {
					active = seg;
					break;
				}
			}
			const activeSegmentName = active?.stats.segmentName ?? null;

			if (!!this.lastActiveSegment[type] && this.lastActiveSegment[type].stats.segmentName === activeSegmentName) {
				continue; //don't fire duplicate
			}

			//this is handle when during seeking or segment fetching takes longer time
			// we don't want to clear the active segment if we still have the last active segment
			if(!active && this.lastActiveSegment[type] != null) {
				//we need this because we want to emit the lastActiveSegment once;
				if (this.lastActiveSegmentProcessed[type]) {
					continue;
				} else {
					this.lastActiveSegmentProcessed[type] = true;
				}

			} else if (active) {
				this.lastActiveSegmentProcessed[type] = false;
				this.lastActiveSegment[type] = active;
			}

			this.currentStatsSubject$.next({
				type: type as SegmentType,
				activeSegmentStats: active?.stats,
				overallVerified: this.overallVerified[type],
				errorSegments: this.errorSegments[type] || [],
				currentTime,
				lastActiveSegment: active ? null : this.lastActiveSegment[type],
				contentVideoShareStopped: false
			});
		}
	}

	public onContentVideoDestroyed(): void {
		this.currentStatsSubject$.next({
			type: 'contentVideo',
			activeSegmentStats: null,
			overallVerified: this.overallVerified.contentVideo,
			errorSegments: this.errorSegments.contentVideo || [],
			lastActiveSegment: this.lastActiveSegment.contentVideo,
			contentVideoShareStopped: true
		});
	}

	/** Clean up */
	public destroy(): void {
		this.currentTimeSub?.unsubscribe();
		this.currentStatsSubject$.next(null);
		this.lastActiveSegment = {
			audio: null,
			video: null,
			contentVideo: null
		};
		this.lastCurrentTimeInSec = 0;
		this.segmentMap = {
			video: new Map<string, Segment>(),
			audio: new Map<string, Segment>(),
			contentVideo: new Map<string, Segment>()
		};
		this.overallVerified = {
			video: true,
			audio: true,
			contentVideo: true
		};
		this.errorSegments = {
			video: [],
			audio: [],
			contentVideo: []
		};
		this.lastActiveSegmentProcessed = {
			audio: false,
			video: false,
			contentVideo: false
		};
	}
}

export const c2paStatReporterInstance = new VbC2paStatsReporter();
